import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router'
import { api, ApiError } from '../lib/api'
import ControlPanel from '../components/control/ControlPanel'
import { DraftProvider } from '../state/DraftProvider'
import type { DraftResponse, DraftState } from '../types/protocol'

// The commissioner's control panel — where Start lands, and where /setup
// forwards once a draft is running. Same credential as the setup console: the
// token in the URL is sent as a header on every request and stored nowhere.
//
// Split from /setup rather than sharing it because the two halves have nothing
// in common. Setup is a stack of forms that the engine refuses after the first
// pick; this is controls over live state, and it wants the cable rather than
// the focus-triggered refetch that setup gets by.
export default function Control() {
  // The token rides in the fragment, which browsers never put in the request
  // line — so no log at any layer (Rails, Thruster, kamal-proxy, Cloudflare)
  // can hold it. A Rails-side filter would only have cleaned one of the four.
  const commissionerToken = decodeURIComponent(useLocation().hash.slice(1))
  const [draft, setDraft] = useState<DraftState | null>(null)
  const [error, setError] = useState<string | null>(null)

  // One resolve, for the token check and the join code. Everything the panel
  // renders after that arrives on the cable, which DraftProvider owns.
  useEffect(() => {
    let cancelled = false
    api<DraftResponse>('/drafts/resolve', { commissionerToken })
      .then((response) => {
        if (cancelled) return
        setDraft(response.draft)
        setError(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof ApiError && e.status === 401
            ? 'This control link is not valid. Double-check the URL you were given.'
            : 'Could not load the draft. Is the server up?',
        )
      })
    return () => {
      cancelled = true
    }
  }, [commissionerToken])

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 text-center">
        <p className="max-w-sm text-ink-mid">{error}</p>
      </main>
    )
  }

  if (!draft) {
    return (
      <main className="flex min-h-screen items-center justify-center text-sm text-ink-lo">
        Loading…
      </main>
    )
  }

  // Nothing to control before the first pick, and the two pages point at each
  // other so either link works whatever the draft's status is.
  if (draft.status === 'setup') {
    return <Navigate to={`/setup#${commissionerToken}`} replace />
  }

  return (
    <main className="min-h-screen">
      <div className="mx-auto max-w-4xl space-y-5 px-4 py-10">
        {/* Stays put while the sections below scroll, so the code you are
            reading out loud is always on screen. */}
        <header className="sticky top-0 z-20 -mx-4 flex flex-wrap items-end justify-between gap-3 border-b border-line bg-surface-2/95 px-4 py-4 shadow-raised backdrop-blur-sm">
          <div className="min-w-0">
            <p className="eyebrow">Control panel</p>
            <h1 className="mt-1 truncate font-display text-3xl leading-none font-bold text-ink-hi">
              {draft.name}
            </h1>
          </div>
          <p className="shrink-0 rounded-full border border-signal/30 bg-signal/10 px-4 py-1.5 font-mono text-lg font-semibold tracking-[0.2em] text-signal">
            {draft.join_code}
          </p>
        </header>

        <DraftProvider code={draft.join_code}>
          <ControlPanel commissionerToken={commissionerToken} />
        </DraftProvider>
      </div>
    </main>
  )
}
